import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { LaeSpecialist } from '@/lib/types';

/**
 * The LAE representatives a tracker row's Specialist may name, read once per
 * mount from `GET /lae-specialists` (which reads the SEED admin system).
 *
 * ⚠️ **`specialists: null` means NOT READ OR FAILED — never `[]`.** An empty
 * list would render as "there are no specialists" and the picker would offer
 * only "No specialist", so the next save would look like a deliberate clear.
 * A failure keeps `null` and carries the server's own sentence in `error`
 * (a 502 naming the upstream, or `LAE_ACCESS_DENIED` naming the RBAC grant),
 * which is the only actionable half.
 *
 * `enabled: false` issues no request and leaves `specialists` at `null`, so a
 * caller that never needs the list never spends a call against the upstream.
 */
export function useLaeSpecialists(enabled: boolean): {
  specialists: LaeSpecialist[] | null;
  loading: boolean;
  error: string | null;
} {
  const [specialists, setSpecialists] = useState<LaeSpecialist[] | null>(null);
  // Starts true when enabled so the first paint is a loading picker rather
  // than an empty one, and nothing sets state synchronously in the effect.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listLaeSpecialists(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSpecialists(result.data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setSpecialists(null);
        setError(
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : 'Could not reach the server to read the specialists.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { specialists, loading: enabled && loading, error };
}
